import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft, ChevronRight, Package, Activity, Download, Box,
  Pencil, MapPin, User, PackageCheck, Truck, ExternalLink, Store,
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { StatusBadge } from '../../../../components/deliveries/StatusBadge';
import { selectHasFulfillmentPro, selectHasPickupPoints } from '../../../../features/auth/authSelectors';
import { DropdownMenu } from '../../../../components/ui/DropdownMenu';
import { fetchOrderDetails, fulfillOrder } from '../../../../features/orderDetails/orderDetailsThunks';
import { clearOrderDetails, setActiveTab } from '../../../../features/orderDetails/orderDetailsSlice';
import { fetchDeliveryItems, fetchAllDeliveryItemLabels } from '../../../../features/deliveryItems/deliveryItemsThunks';
import {
  canEditOrderInfo,
  getOrderSourceLabel,
  formatDateTime,
} from '../../../../features/orderDetails/orderUtils';
import { mergePDFSFromUrls } from '../../../../services/pdfService';
import OrderGeneralTab from './components/OrderGeneralTab';
import OrderProductsTab from './components/OrderProductsTab';
import OrderPackagesTab from './components/OrderPackagesTab';
import OrderHistoryTab from './components/OrderHistoryTab';
import EditShippingModal from './modals/EditShippingModal';
import EditRecipientModal from './modals/EditRecipientModal';
import ChangeCourierModal from './modals/ChangeCourierModal';
import EditStatusModal from './modals/EditStatusModal';
import PickupPointModal from './modals/PickupPointModal';
import HandOverModal from './modals/HandOverModal';
import {
  canHandOverAtPickupPoint,
  canRedirectToPickupPoint,
} from '../../../../features/pickupPoints/pickupPointsUtils';

export const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: order, loading, loadError, activeTab } = useSelector((state) => state.orderDetails);
  // The bultos count comes from the delivery-items slice, the same source the
  // Bultos tab paginates over. Reading it off the order would let the tab label
  // and the table disagree.
  const { count: itemsCount } = useSelector((state) => state.deliveryItems);
  const { user } = useSelector((state) => state.auth);
  const isAdmin = user?.user_type?.includes('admin');
  const hasFulfillmentPro = useSelector(selectHasFulfillmentPro);
  const hasPickupPoints = useSelector(selectHasPickupPoints);

  const [actionError, setActionError] = useState('');
  const [fulfilling, setFulfilling] = useState(false);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  // Held here rather than inside a tab: the actions live in the hero, so they
  // must stay reachable no matter which tab is open.
  const [openModal, setOpenModal] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    dispatch(fetchOrderDetails(orderId));
    dispatch(fetchDeliveryItems(orderId));
    return () => {
      dispatch(clearOrderDetails());
    };
  }, [orderId, dispatch]);

  const productCount = order?.products?.length || 0;

  const tabs = useMemo(() => {
    const base = [{ id: 'general', label: 'Resumen de Orden', icon: Activity }];
    // Only orders that carry lines can be picked; an Excel order has none.
    if (productCount > 0) {
      base.push({ id: 'productos', label: `Productos (${productCount})`, icon: PackageCheck });
    }
    base.push(
      { id: 'bultos', label: `Bultos (${itemsCount})`, icon: Box },
      { id: 'historial', label: 'Historial', icon: Activity }
    );
    return base;
  }, [itemsCount, productCount]);

  const handleFulfill = async () => {
    if (!order?.delivery?.id) {
      setActionError('Esta orden no tiene un envío asociado que preparar.');
      return;
    }
    setActionError('');
    setFulfilling(true);
    try {
      await dispatch(fulfillOrder({ orderId, deliveryId: order.delivery.id })).unwrap();
      dispatch(fetchDeliveryItems(orderId));
    } catch (thunkError) {
      setActionError(thunkError);
    } finally {
      setFulfilling(false);
    }
  };

  /**
   * Merges the label of every bulto into a single PDF and opens it.
   *
   * The labels are re-fetched unpaginated: the table may be showing page 2 of
   * 5, and printing only the visible page would leave parcels unlabelled.
   */
  const handleDownloadLabel = async () => {
    setActionError('');
    setDownloadingLabel(true);
    try {
      const labels = await dispatch(fetchAllDeliveryItemLabels(orderId)).unwrap();
      if (labels.length === 0) {
        setActionError('Esta orden todavía no tiene etiquetas generadas.');
        return;
      }
      const merged = await mergePDFSFromUrls(labels);
      window.open(URL.createObjectURL(merged), '_blank', 'noopener,noreferrer');
    } catch (thunkError) {
      // The thunk rejects with a string; the merge throws an Error that names
      // the label it could not read, and that name is the useful part.
      setActionError(
        typeof thunkError === 'string'
          ? thunkError
          : thunkError?.message || 'Ocurrió un error al descargar la etiqueta'
      );
    } finally {
      setDownloadingLabel(false);
    }
  };

  if (loading && !order) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-ninja-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (loadError && !order) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-danger p-6 rounded-2xl border border-red-200 dark:border-red-900">
        <h3 className="font-bold text-lg mb-2">Error al cargar orden</h3>
        <p>{loadError}</p>
        <Button
          variant="secondary"
          icon={ArrowLeft}
          onClick={() => navigate('/deliveries')}
          className="mt-4"
        >
          Volver a Deliveries
        </Button>
      </div>
    );
  }

  if (!order) return null;

  const canEditShipping = canEditOrderInfo(order);

  const editItems = [
    {
      label: 'Editar información de entrega',
      icon: MapPin,
      onClick: () => setOpenModal('shipping'),
      disabled: !canEditShipping,
      description: canEditShipping
        ? undefined
        : 'Solo mientras la orden está en preparación',
    },
    {
      label: 'Editar destinatario',
      icon: User,
      onClick: () => setOpenModal('recipient'),
    },
    // Choosing who carries the parcel is what Fulfillment Pro sells. Without
    // it the courier is a fact of the shipment, shown but not chosen.
    ...(hasFulfillmentPro
      ? [{
        label: 'Cambiar courier',
        icon: Truck,
        onClick: () => setOpenModal('courier'),
        disabled: !order.delivery?.id,
      }]
      : []),
    // Pickup points are the operator's: only the admin redirects a shipment to
    // one, and only before the courier has it. The hand-over is the one step
    // that closes a shipment left at a point.
    ...(isAdmin && hasPickupPoints
      ? [{
        label: order.delivery?.pickup_point
          ? 'Cambiar punto de retiro'
          : 'Entregar en punto de retiro',
        icon: Store,
        onClick: () => setOpenModal('pickupPoint'),
        disabled: !canRedirectToPickupPoint(order.delivery),
        description: canRedirectToPickupPoint(order.delivery)
          ? undefined
          : "Solo mientras la orden está 'Pendiente' o 'Por asignar'",
      }]
      : []),
    ...(isAdmin && hasPickupPoints && canHandOverAtPickupPoint(order.delivery)
      ? [{
        label: 'Confirmar entrega en punto de retiro',
        icon: PackageCheck,
        onClick: () => setOpenModal('handOver'),
        description: 'El punto entregó el pedido al cliente final',
      }]
      : []),
    ...(isAdmin
      ? [{
        label: 'Cambiar estado de entrega',
        icon: Activity,
        onClick: () => setOpenModal('status'),
        danger: true,
        dividerBefore: true,
        description: 'Afecta a todos los bultos de la orden',
      }]
      : []),
  ];

  return (
    <div className="space-y-8 animate-in fade-in duration-300">

      {/* Breadcrumb / Back Navigation */}
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <button
          onClick={() => navigate('/deliveries')}
          className="hover:text-ninja-dark dark:hover:text-white transition-colors flex items-center gap-1"
        >
          <ArrowLeft size={14} /> Volver a Deliveries
        </button>
        <ChevronRight size={14} className="text-border" />
        <span className="font-semibold text-ninja-dark dark:text-white">Orden #{order.reference_id}</span>
      </div>

      {/* A failed reload leaves the order on screen, under a strip. */}
      {loadError && (
        <StaleDataNotice
          testId="order-details-stale-error"
          error={loadError}
          onRetry={() => dispatch(fetchOrderDetails(orderId))}
        />
      )}

      {/* Hero Header */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl p-8 shadow-[0_4px_24px_-12px_rgba(0,0,0,0.1)] border border-border relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-ninja-green/5 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none"></div>

        <div className="flex items-center gap-6 relative z-10">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-ninja-greenLight to-ninja-green/20 flex items-center justify-center border border-ninja-green/30 text-ninja-green shadow-inner">
            <Package size={40} strokeWidth={1.5} />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h1 className="text-3xl font-extrabold text-ninja-dark dark:text-white tracking-tight">
                #{order.reference_id}
                {order.external_pack_id && (
                  <span className="text-text-muted font-bold">-{order.external_pack_id}</span>
                )}
              </h1>
              <span className="px-3 py-1 bg-ninja-greenLight text-ninja-green rounded-full text-xs font-bold shadow-sm border border-ninja-green/20">
                {order.seller?.company?.social_reason || 'Vendedor'}
              </span>
              <span className="px-3 py-1 bg-ninja-gray dark:bg-[#2d2d2d] text-text-secondary rounded-full text-xs font-bold shadow-sm border border-border">
                Origen: {getOrderSourceLabel(order.source)}
              </span>
            </div>
            {/* The status takes the delivery's own — the same one the listing
                shows — so that going from the table to here says the same
                thing. */}
            <div className="text-text-secondary flex items-center gap-2 mb-3 flex-wrap">
              <span>ID Interno: {order.id}</span>
              <StatusBadge
                status={order.delivery?.status || order.mixed_status}
                substatus={order.delivery?.substatus}
                variant="inline"
                testId="order-details-status"
              />
            </div>
            <div className="flex flex-col gap-2 text-sm font-medium items-start">
              <div className="flex items-center gap-1.5 text-text-muted">
                Creado el {formatDateTime(order.created_at)}
              </div>
              {/* Public tracking page. It lives here rather than among the
                  delivery data because it is what gets sent to the customer,
                  and it has to be at hand from any tab. */}
              <a
                href={`/track-order/${order.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-ninja-green hover:underline flex items-center gap-1.5"
              >
                Tracking pedido <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>

        <div className="relative z-20 flex flex-col gap-3 w-full md:w-auto">
          <Button
            variant="primary"
            icon={PackageCheck}
            onClick={handleFulfill}
            loading={fulfilling}
            loadingLabel="Preparando..."
            fullWidth
          >
            Preparar Envío
          </Button>
          <Button
            variant="secondary"
            icon={Download}
            onClick={handleDownloadLabel}
            loading={downloadingLabel}
            loadingLabel="Generando..."
            fullWidth
          >
            Descargar Etiqueta
          </Button>
          <DropdownMenu triggerLabel="Editar" triggerIcon={Pencil} items={editItems} />
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 dark:bg-red-900/20 text-danger px-5 py-3 rounded-2xl border border-red-200 dark:border-red-900 text-sm font-medium flex items-center justify-between gap-4">
          <span>{actionError}</span>
          <button onClick={() => setActionError('')} className="font-bold shrink-0">Cerrar</button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="flex items-center gap-8 border-b border-border/60">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => dispatch(setActiveTab(tab.id))}
              className={`flex items-center gap-2 pb-4 text-sm font-bold transition-all relative ${isActive ? 'text-ninja-green' : 'text-text-muted hover:text-ninja-dark dark:hover:text-white'
                }`}
            >
              <Icon size={16} className={isActive ? 'text-ninja-green' : 'opacity-60'} />
              {tab.label}
              {isActive && (
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-ninja-green rounded-t-full shadow-[0_-2px_10px_rgba(0,192,136,0.5)]"></div>
              )}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'general' && <OrderGeneralTab order={order} onError={setActionError} />}
        {activeTab === 'productos' && <OrderProductsTab order={order} />}
        {activeTab === 'bultos' && <OrderPackagesTab order={order} />}
        {activeTab === 'historial' && <OrderHistoryTab order={order} />}
      </div>

      <EditShippingModal
        isOpen={openModal === 'shipping'}
        onClose={() => setOpenModal(null)}
        order={order}
        onError={setActionError}
      />
      <EditRecipientModal
        isOpen={openModal === 'recipient'}
        onClose={() => setOpenModal(null)}
        order={order}
        onError={setActionError}
      />
      <ChangeCourierModal
        isOpen={openModal === 'courier'}
        onClose={() => setOpenModal(null)}
        order={order}
        onError={setActionError}
      />
      <EditStatusModal
        isOpen={openModal === 'status'}
        onClose={() => setOpenModal(null)}
        order={order}
        onError={setActionError}
      />
      <PickupPointModal
        isOpen={openModal === 'pickupPoint'}
        onClose={() => setOpenModal(null)}
        order={order}
      />
      <HandOverModal
        isOpen={openModal === 'handOver'}
        onClose={() => setOpenModal(null)}
        order={order}
      />
    </div>
  );
};
